import React, { useEffect, useReducer } from 'react'
import reducer from '../../utils/CommonReducer'
import Pagination from '../../../Pagination'
import Avatar from '../../../design_system/components/Avatar/Avatar.jsx'
import { Button } from '../../../design_system/components/Button/Button.jsx'
import { Popup } from '../../../design_system/components/Popup/Popup.jsx'
import { Loader } from '../../../design_system/components/Loader.jsx'
import { Tooltip } from '../../../design_system/components/ToolTip/ToolTip.jsx'
import { Icons } from '../../../design_system/components/Icons'
import { useToast } from '../../../design_system/components/Toast/ToastContext.jsx'
import SelectPublishPopup from '../../cta_utils/CommonPopups.js/PublishPopups/SelectPublishPopup.jsx'
import { export_mapper_posts, fetch_unmapped_job_updates } from '../../admin_views/new_temp_mapper/api'
import { indexOfObject } from '../../utils/ArrayUtils'
import ConditionalRender from '../../../ConditionalRender'
import { JobUpdateDesc } from '../../feed_components/JobUpdateDesc'
import NewDropdownGroupFilter from '../../cta_utils/NewDropDownGroupFilter'
import { TempPostFilterOptions } from '../../utils/PostFilterOptions'
import { SUCCESS_MESSAGES, WARNING_MESSAGES } from '../../utils/SuccessMessages'
import { useSelector } from 'react-redux'
import useCheckUserAccess from '../../custom_hooks/useCheckUserAccess'
import { canUsePolicyAction } from '../../../utils/access'

const UnmappedJobUpdatesLowConfidence = ({ type = 'default', setParentComponentState, buttonLabel = "Map and Save", ...props }) => {
    const toast = useToast()
    const currentTeam = useSelector(state => state.current_team.team)
    const checkUserAccess = useCheckUserAccess()
    const canPublishJobUpdates = canUsePolicyAction({
        permissionKey: "jobs.publish",
        team: currentTeam,
        legacyAllowed: checkUserAccess("news_publishing"),
    })

    const [componentData, setComponentData] = useReducer(reducer, {
        jobUpdates: [],
        selectedJobUpdate: {},
        totalPages: null,
        totalCount: null,
        currentPage: 1,
        isLoading: {},
        popupsList: {},
        dropDownFilterlist: { containSuggestion: 'all' },
    })

    const setComponentState = (payload) => setComponentData({ type: "SET_STATE", payload })
    const { totalPages, currentPage, jobUpdates, popupsList } = componentData
    let { dropDownFilterlist } = componentData
    if (props.dropDownFilterlist) dropDownFilterlist = props.dropDownFilterlist
    useEffect(() => {
        setComponentState({ isLoading: { isFetching: true } })
        let data_params = get_data_params();
        data_params["page"] = currentPage
        fetch_unmapped_job_updates(data_params).then(res => {
            setComponentState({
                totalPages: res.data.total_pages,
                totalCount: res.data.total,
                jobUpdates: [...res.data.data],
                isLoading: {},
            })
            setParentComponentState?.({ unmappedCount: res.data.total, mappedCount: res.data.mapped_count })
        })
    }, [currentPage, dropDownFilterlist])

    const get_data_params = () => {
        let data_params = {}
        if (dropDownFilterlist.containSuggestion == 'contains_suggestions') {
            data_params["has_sugg"] = true;
        }
        else if (dropDownFilterlist.containSuggestion == 'no_suggestions') {
            data_params["has_sugg"] = false;
        }
        return data_params
    }
    const onFinishPosting = (mapped_id, result = {}) => {
        if (result.mapped === false && !result.gone) {
            toast.error("That prospect no longer exists. Nothing was mapped.")
            setComponentState({ popupsList: {} })
            return
        }
        let index = indexOfObject(jobUpdates, "id", mapped_id)
        if (index !== -1) {
            let temp = [...jobUpdates]
            temp.splice(index, 1)
            let success_message = buttonLabel == 'Move to High Confidence' ? "Job update moved to high confidence" : "Prospect mapped and post saved."
            if (result.job_update_result === 'existing') success_message = "This job update already exists — no duplicate was created."
            else if (result.job_update_result === 'job_updates_disabled') success_message = "Prospect mapped."
            setComponentState({ jobUpdates: temp, popupsList: {} })
            toast.success(success_message)
        }
    }
    const renderLinkedinProspect = (linkedin_prospect) => {
        return (
            <a href={linkedin_prospect.linkedin_url}
                className="flex items-center flex-1 min-w-0 no-underline newsfeed__prospect-container newsfeed__prospect-container--link"
            >
                <div className="newsfeed__prospect-info">
                    <Avatar
                        width={40}
                        name={linkedin_prospect.name}
                        profile_image={linkedin_prospect.profile_image}
                    />
                    <div className="flex flex-col flex-1 min-w-0">
                        <Tooltip
                            tooltipText={() => linkedin_prospect.name}
                            placement='bottom'
                        >
                            <div className="body-m-bold truncate newsfeed__post-title">{linkedin_prospect.name}</div>
                        </Tooltip>
                        <Tooltip
                            tooltipText={() => `${linkedin_prospect.designation}, ${linkedin_prospect.company}`}
                            placement='bottom'
                        >
                            <div className="body-s-semibold truncate newsfeed__item-subtitle newsfeed__item-subtitle--padded-right">{linkedin_prospect.designation}, {linkedin_prospect.company}</div>
                        </Tooltip>
                    </div>
                </div>
            </a>
        )
    }
    const setPopupsList = (payload) => {
        setComponentState({ popupsList: payload })
    }
    const submitMapperJobUpdate = () => {
        export_mapper_posts({ mapper_type: 'TempPost', ...get_data_params() }).then(res => {
            setComponentState({ popupsList: {} })
            toast.success(SUCCESS_MESSAGES['export'].message)
        }).catch(error => {
            setComponentState({ popupsList: {} })
            if (error.response.status === 403)
                toast.warning(WARNING_MESSAGES['export'].message)
        })
    }

    const exportPopup = () => {
        return (
            <Popup
                name="Unmapped Job Updates"
                handleOnClosePopup={() => setPopupsList({})}
                maxWidth={350}
                showHeaderDivider={false}
                showFooterDivider={false}
                primaryAction={{ label: 'Export', onClick: () => submitMapperJobUpdate() }}
                secondaryAction={{ label: 'Cancel', onClick: () => setPopupsList({}) }}
            >
                <div className="body-m-bold">Do you want to export unmapped job updates?</div>
            </Popup>
        )
    }
    if (componentData.isLoading.isFetching) return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, minHeight: '50vh' }}>
            <Loader isLoading={true} Size="Large" />
        </div>
    )
    return (
        <div className="job-updates__container mb-xl">
            <ConditionalRender if={type === 'feed' && !componentData.isLoading.isFetching && jobUpdates.length === 0}>
                <div className="job-updates__empty-state">
                    <div className="body-l-bold">No Low Confidence results to show!</div>
                </div>
            </ConditionalRender>
            <ConditionalRender if={!componentData.isLoading.isFetching && jobUpdates.length > 0}>
                <div className="job-updates__filters-bar flex justify-between items-center">
                    <div></div>
                    <Pagination totalPages={totalPages} currentPage={currentPage} onPageChange={(page) => setComponentState({ currentPage: page })} style={{ padding: 0 }} />
                    <div>
                        <ConditionalRender if={type !== 'feed'}>
                            <div className="job-updates__filters-right flex items-center">
                                <div className="job-updates__filter-gap">
                                    <NewDropdownGroupFilter
                                        clearFilterCallBack={(close) => {
                                            setComponentState({ dropDownFilterlist: { containSuggestion: 'all' } });
                                            close?.();
                                        }}
                                        filterList={dropDownFilterlist}
                                        onChangeCallback={(group, option) => setComponentState({ dropDownFilterlist: { ...dropDownFilterlist, [group]: option } })}
                                        group_options={TempPostFilterOptions}
                                    />
                                </div>
                                <div onClick={() => { setPopupsList({ exportPopup: true }) }}>
                                    <Tooltip
                                        tooltipText={() => "Export"}
                                        showWithoutOverflowing={true}
                                        placement="bottom"
                                    >
                                        <div className={`newsfeed__icon-button icon-container`}>
                                            <Icons name="download" size={24} />
                                        </div>
                                    </Tooltip>
                                </div>
                            </div>
                        </ConditionalRender>
                    </div>
                </div>
                {
                    jobUpdates.map((jobUpdate) => {
                        let maxConfidence = jobUpdate.prospect_suggestions?.length > 0 ? Math.max(...jobUpdate.prospect_suggestions.map(item => item.similarity)) : null;
                        return (
                            <div key={jobUpdate.id} className="newsfeed__post-card">
                                <div className="flex justify-between items-center mx-xl gap-m">
                                    {renderLinkedinProspect(jobUpdate.linkedin_prospect)}
                                    <div className="flex items-center flex-row gap-s">
                                        <ConditionalRender if={maxConfidence}>
                                            <div className="body-s-semibold color-text-secondary">Top confidence: <span className="body-s-bold color-text-primary">{maxConfidence}%</span></div>
                                        </ConditionalRender>
                                        <ConditionalRender if={canPublishJobUpdates}>
                                            <Button Size="Small" onClick={() => setComponentState({ selectedJobUpdate: jobUpdate, popupsList: { mapAndPostPopup: true } })}>{buttonLabel}</Button>
                                        </ConditionalRender>
                                        <Tooltip
                                            tooltipText={() => jobUpdate.formatted_created_at}
                                            showWithoutOverflowing={true}
                                            placement="top"
                                        >
                                            <div className="body-s-semibold newsfeed__item-subtitle cursor-default">{jobUpdate.created_at_time}</div>
                                        </Tooltip>
                                    </div>
                                </div>
                                <div className="newsfeed__divider my-m mx-0"></div>
                                <div className="mx-xl">
                                    <JobUpdateDesc job_update={{ ...jobUpdate, name: jobUpdate.linkedin_prospect.name }} />
                                </div>
                            </div>
                        )
                    })
                }
            </ConditionalRender>
            {canPublishJobUpdates && popupsList.mapAndPostPopup && <SelectPublishPopup post_type="Post::JobUpdates" source="low_confidence_feed" onCloseCallback={() => setComponentState({ popupsList: {} })}
                onPublishCallback={onFinishPosting}
                selectedJobUpdate={componentData.selectedJobUpdate}
            />}
            {popupsList.exportPopup && exportPopup()}
        </div>
    )
}

export default UnmappedJobUpdatesLowConfidence
